import React, { useContext, useEffect } from "react";
import { LanguageContext } from "../Context/LanguageContext";
import AOS from "aos";
import "aos/dist/aos.css";
import AboutImage from "../assets/About.jpg";
import SectionHeader from "../components/SectionHeader";

const AboutSection = () => {
  const { texts, language } = useContext(LanguageContext);
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false
    });
  }, []);
  
  return (
    <div
      id="about"
      className="relative py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-white via-neutral-50 to-primary-50/30 overflow-hidden scroll-mt-20 sm:scroll-mt-24"
    >
      {/* Animated background patterns */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-200 rounded-full opacity-20 blur-3xl animate-pulse animation-delay-1000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
        {/* Enhanced Section Header */}
        <SectionHeader
          badgeText={texts.badge_our_story}
          badgeIcon="dot"
          title={texts.about_us}
          titleAs="h2"
        />

        {/* Enhanced Main Content Card with 3D effect */}
        <div 
          className="relative group"
          data-aos="fade-up"
          data-aos-duration="1000"
        >
          {/* Glowing shadow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-400 rounded-3xl sm:rounded-[2.5rem] opacity-20 group-hover:opacity-30 blur-xl transition duration-500"></div>
          
          {/* Main card */}
          <div className="relative bg-white rounded-3xl sm:rounded-[2.5rem] shadow-2xl overflow-hidden border border-neutral-100">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Text Content with enhanced styling */}
              <div className="relative p-6 sm:p-10 md:p-12 lg:p-16 flex flex-col justify-center">
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-bl-full opacity-50"></div>
                
                <div
                  className="relative space-y-6 sm:space-y-8"
                  data-aos="fade-right"
                  data-aos-delay="300"
                >
                  {/* Icon or decorative element */}
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>

                  <p className="text-neutral-700 text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed font-medium py-2">
                    <span className="inline-block px-1" style={{ lineHeight: '1.5' }}>
                      {texts.about_section_text}
                    </span>
                  </p>

                  {/* Feature highlights */}
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-neutral-900 font-bold text-sm sm:text-base">{texts.feature_quality_first_title}</h4>
                        <p className="text-neutral-600 text-xs sm:text-sm">{texts.feature_quality_first_subtitle}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-neutral-900 font-bold text-sm sm:text-base">{texts.feature_best_price_title}</h4>
                        <p className="text-neutral-600 text-xs sm:text-sm">{texts.feature_best_price_subtitle}</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="pt-4">
                    <a
                      href="/about"
                      className={`group/btn relative inline-flex items-center gap-3 overflow-hidden bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 hover:from-primary-500 hover:via-primary-600 hover:to-primary-700 text-white font-bold px-6 sm:px-10 py-3 sm:py-4 rounded-2xl transition-all duration-500 transform hover:scale-105 hover:shadow-2xl text-sm sm:text-base md:text-lg `}
                    >
                      <span className="relative z-10">{texts.read_more}</span>
                      <svg
                        className={`relative z-10 w-5 h-5 transition-transform duration-300 ${language === 'ar' ? 'group-hover/btn:-translate-x-2 rotate-180' : 'group-hover/btn:translate-x-2'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"></div>
                    </a>
                  </div>
                </div>
              </div>

              {/* Enhanced Image Content */}
              <div className="relative lg:order-last h-64 sm:h-80 lg:h-full min-h-[400px] group/img">
                <div className="absolute inset-0 overflow-hidden">
                  <img
                    className="w-full h-full object-cover transform group-hover/img:scale-110 transition-transform duration-700"
                    src={AboutImage}
                    alt="About us"
                    data-aos="fade-left"
                    data-aos-delay="400"
                  />
                  {/* Multi-layered overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 via-transparent to-primary-900/20"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  
                  {/* Decorative corner frame */}
                  <div className="absolute top-4 left-4 w-16 h-16 border-t-4 border-l-4 border-white/50 rounded-tl-2xl"></div>
                  <div className="absolute bottom-4 right-4 w-16 h-16 border-b-4 border-r-4 border-white/50 rounded-br-2xl"></div>
                  
                  {/* Floating badge */}
                  <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-lg">15+</span>
                      </div>
                      <div>
                        <p className="text-neutral-900 font-bold text-sm">{texts.badge_years_of}</p>
                        <p className="text-neutral-600 text-xs">{texts.badge_excellence}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Decorative floating elements */}
        <div className="absolute top-32 left-4 w-20 h-20 bg-primary-200 rounded-2xl opacity-30 rotate-12 hidden xl:block" data-aos="fade-right" data-aos-delay="800"></div>
        <div className="absolute bottom-20 right-8 w-16 h-16 bg-primary-300 rounded-full opacity-40 hidden xl:block" data-aos="fade-left" data-aos-delay="1000"></div>
        <div className="absolute top-1/2 right-4 w-12 h-12 bg-primary-100 rounded-lg opacity-50 -rotate-12 hidden xl:block" data-aos="fade-left" data-aos-delay="1200"></div>
      </div>
    </div>
  );
};

export default AboutSection;
